import React, { useContext, useEffect, useState } from 'react';
import { useAuth0 } from '@auth0/auth0-react';
import { useTranslation } from 'react-i18next';
import {
  isPayoutAccountConfirmationRequired,
  PayoutAccount,
  PayoutProvider,
  PayoutProviderDisplay,
  WiseConfirmationOutcome,
} from '../../utils/types/Entity';
import {
  AddBankAccountContainerProps,
  WiseCreateAccountOptions,
} from './AddBankAccountContainer.types';
import AddPayoutAccountBaseModule from '../AddPayoutAccountBaseModule/AddPayoutAccountBaseModule';
import { getTransferKind } from '../../utils/helpers/transferKind';
import AddPayoutAccountIdentifierForm from '../../components/AddPayoutAccountIdentifierForm/AddPayoutAccountIdentifierForm';
import styles from './add-bank-account-container.module.scss';
import { UKSortCodeBankForm } from '../../components/AddPayoutAccountUKSortCodeForm/AddPayoutAccountUKSortCodeFormProps.types';
import { IBANForm } from '../../components/AddPayoutAccountIBANForm/AddPayoutAccountIBANForm.types';
import { IdentifierForm } from '../../components/AddPayoutAccountIdentifierForm/AddPayoutAccountIdentifierForm.types';
import { FORM_ID } from '../../utils/validate';
import { AddACHAccountContainer } from '../index';
import AddPayoutAccountUKBankWrapper from '../../components/AddPayoutAccountUKBankWrapper/AddPayoutAccountUKBankWrapper';
import Typography from '../../sharedComponents/Typography/Typography';
import AddPayoutAccountIBANForm from '../../components/AddPayoutAccountIBANForm/AddPayoutAccountIBANForm';
import VerificationOfPayeeComponent from '../../components/VerificationOfPayeeComponent/VerificationOfPayeeComponent';
import AddPayoutAccountConfirmation from '../../components/AddPayoutAccountConfirmation/AddPayoutAccountConfirmation';
import { PayoutAccountAPI } from '../../api';
import { CountryCode } from '../../utils/types/CountryCode/CountryCode.types';
import { PayoutProviderCustomerType } from '../../utils/types/Entity';
import { ConfigContext } from '../../contexts';
import { useAppSelector } from '../../store/hooks';
import { getAvailableProviders } from '../../store/slices';

const BANK_ACCOUNT_STEPS = {
  ACCOUNT_TYPE: 0,
  TRANSFER_DETAILS: 1,
  VERIFY_PAYEE: 2,
  ACCOUNT_CONFIRMATION: 3,
  COMPLETE: 4,
} as const;

type BankAccountStep =
  (typeof BANK_ACCOUNT_STEPS)[keyof typeof BANK_ACCOUNT_STEPS];

const AddBankAccountContainer: React.FC<AddBankAccountContainerProps> = ({
  onBackClick,
  onAccountCreationSuccess,
}) => {
  const provider = PayoutProviderDisplay.Bank;
  const [step, setStep] = useState<number>(BANK_ACCOUNT_STEPS.ACCOUNT_TYPE);
  const [formComplete, setFormComplete] = useState(false);
  const [error, setError] = useState('');
  const [formIdentifier, setFormIdentifier] = useState<IdentifierForm>({
    countryCode: '',
    bankCurrency: undefined,
  });

  const availableProviders = useAppSelector(
    getAvailableProviders(formIdentifier.bankCurrency),
  );
  const [formSubmissionResult, setFormSubmissionResult] = useState<
    'success' | 'failed' | 'confirmation_required' | null
  >(null);
  const [payoutAccount, setPayoutAccount] = useState<PayoutAccount | null>(
    null,
  );
  const [recommendedUpdates, setRecommendedUpdates] = useState<
    WiseConfirmationOutcome[]
  >([]);
  const [confirmationAccountID, setConfirmationAccountID] = useState<
    string | null
  >(null);
  const [submitDwolla, setSubmitDwolla] = useState<null | (() => void)>(null);

  const { t } = useTranslation();

  const { getAccessTokenSilently } = useAuth0();
  const { user } = useAppSelector((state) => state.user);

  const configEnvelope = useContext(ConfigContext);
  const wiseEmailWhitelist = configEnvelope.config?.wiseEmailWhitelist;

  const BANK_ACCOUNT_STEP_TITLES: Record<BankAccountStep, string> = {
    [BANK_ACCOUNT_STEPS.ACCOUNT_TYPE]: t('sunshine.select_account_type.header'),
    [BANK_ACCOUNT_STEPS.TRANSFER_DETAILS]: t(
      'sunshine.transfer_details.header',
    ),
    [BANK_ACCOUNT_STEPS.VERIFY_PAYEE]: t('sunshine.verify_payee.header'),
    [BANK_ACCOUNT_STEPS.ACCOUNT_CONFIRMATION]: t(
      'sunshine.confirm_account.header',
    ),
    //TODO: remove this step if not using
    [BANK_ACCOUNT_STEPS.COMPLETE]: 'Complete',
  };
  const [selectedForm, setSelectedForm] = useState<{
    id: FORM_ID;
    form:
      | IBANForm
      | UKSortCodeBankForm
      | IdentifierForm
      | Record<string, never>;
  }>({ id: FORM_ID.IDENTIFIER, form: {} });

  useEffect(() => {
    if (step !== BANK_ACCOUNT_STEPS.TRANSFER_DETAILS) return;
    if (formSubmissionResult === null) return;

    if (formSubmissionResult === 'success') {
      setStep(BANK_ACCOUNT_STEPS.ACCOUNT_CONFIRMATION);
      setFormComplete(true);
    } else if (formSubmissionResult === 'confirmation_required') {
      setStep(BANK_ACCOUNT_STEPS.VERIFY_PAYEE);
      setFormComplete(true);
    } else if (formSubmissionResult === 'failed') {
      setFormComplete(false);
    }
  }, [formSubmissionResult, step]);

  const submitFormHandler = async (
    form: IBANForm | UKSortCodeBankForm,
    formID: FORM_ID,
  ): Promise<void> => {
    try {
      setError('');
      const accessToken = await getAccessTokenSilently();
      let options: WiseCreateAccountOptions;

      if (formID === FORM_ID.IBAN) {
        const ibanForm = form as IBANForm;
        options = {
          accountHolderName: ibanForm.accountHolderName,
          currency: ibanForm.currency,
          type: PayoutProviderCustomerType.IBAN,
          details: {
            address: {
              city: ibanForm.city,
              countryCode: ibanForm.countryCode,
              postCode: ibanForm.postalCode,
              firstLine: ibanForm.addressLine,
            },
            legalType: ibanForm.legalType,
            ...(ibanForm.bic?.trim() ? { bic: ibanForm.bic.trim() } : {}),
            iban: ibanForm.iban,
          },
        };
      } else if (formID === FORM_ID.SORT_CODE) {
        const localForm = form as UKSortCodeBankForm;
        options = {
          accountHolderName: localForm.accountHolderName,
          currency: localForm.currency,
          type: PayoutProviderCustomerType.SORT_CODE,
          details: {
            address: {
              city: localForm.city,
              countryCode: localForm.countryCode,
              postCode: localForm.postalCode,
              firstLine: localForm.addressLine,
            },
            legalType: localForm.legalType,
            sortCode: localForm.sortCode,
            accountNumber: localForm.accountNumber,
          },
        };
      } else {
        setFormSubmissionResult('failed');
        return;
      }

      const createdAccount = await PayoutAccountAPI.createPayoutAccount(
        accessToken,
        PayoutProvider.WISE,
        options as unknown as Record<string, string | Record<string, string>>,
        user.langLocale,
      );

      if (isPayoutAccountConfirmationRequired(createdAccount)) {
        setConfirmationAccountID(createdAccount.accountID);
        setRecommendedUpdates(createdAccount.outcomes);
        setFormSubmissionResult('confirmation_required');
        return;
      }

      setConfirmationAccountID(null);
      setRecommendedUpdates([]);
      setPayoutAccount(createdAccount);
      setFormSubmissionResult('success');
    } catch (e) {
      setError(t('error.smth_went_wrong'));
      setFormSubmissionResult('failed');
    }
  };

  const getProvider = (): PayoutProvider | undefined => {
    if (selectedForm.id == FORM_ID.ACH) {
      return PayoutProvider.DWOLLA;
    } else if (
      selectedForm.id == FORM_ID.IBAN ||
      selectedForm.id == FORM_ID.SORT_CODE
    ) {
      return PayoutProvider.WISE;
    }
  };

  const getPayoutProviderCustomerType = ():
    | PayoutProviderCustomerType
    | undefined => {
    switch (selectedForm.id) {
      case FORM_ID.ACH:
        return PayoutProviderCustomerType.DWOLLA;
      case FORM_ID.IBAN:
        return PayoutProviderCustomerType.IBAN;
      case FORM_ID.SORT_CODE:
        return PayoutProviderCustomerType.SORT_CODE;
      default:
        return undefined;
    }
  };

  const acceptVerification = async (): Promise<void> => {
    if (!confirmationAccountID) {
      setError(t('error.cannot_accept_verification'));
      return;
    }

    try {
      const accessToken = await getAccessTokenSilently();
      const provider = getProvider();
      if (!provider) {
        throw new Error('Provider cannot be determined');
      }
      const type = getPayoutProviderCustomerType();
      if (!type) {
        throw new Error('Account type cannot be determined');
      }
      const confirmedAccount = await PayoutAccountAPI.acceptVerificationOfPayee(
        accessToken,
        {
          accountID: confirmationAccountID,
          acceptedOutcomes: true,
          type,
        },
        provider,
        formIdentifier.bankCurrency,
        user.langLocale,
      );
      if (isPayoutAccountConfirmationRequired(confirmedAccount)) {
        setConfirmationAccountID(confirmedAccount.accountID);
        setRecommendedUpdates(confirmedAccount.outcomes);
        setFormSubmissionResult('confirmation_required');
        return;
      }
      setPayoutAccount(confirmedAccount);
      setFormSubmissionResult('success');
      setStep(BANK_ACCOUNT_STEPS.ACCOUNT_CONFIRMATION);
      setFormComplete(true);
    } catch (e) {
      console.error(e);
      setError(t('error.saving_account'));
      setFormSubmissionResult('failed');
    }
  };

  const submitByFormID: Record<string, () => void> = {
    [FORM_ID.IBAN]: async () => {
      await submitFormHandler(selectedForm.form as IBANForm, FORM_ID.IBAN);
    },
    [FORM_ID.SORT_CODE]: async () => {
      await submitFormHandler(
        selectedForm.form as UKSortCodeBankForm,
        FORM_ID.SORT_CODE,
      );
    },
    [FORM_ID.ACH]: () => {
      submitDwolla?.();
    },
  };

  const saveAccountByFormID = () => {
    payoutAccount && onAccountCreationSuccess(payoutAccount);
  };

  const handleSave = async () => {
    switch (step) {
      case BANK_ACCOUNT_STEPS.ACCOUNT_TYPE:
        setStep(BANK_ACCOUNT_STEPS.TRANSFER_DETAILS);
        setFormComplete(false);
        break;
      case BANK_ACCOUNT_STEPS.TRANSFER_DETAILS:
        submitByFormID[selectedForm.id]?.();
        setFormComplete(true);
        break;
      case BANK_ACCOUNT_STEPS.VERIFY_PAYEE:
        await acceptVerification();
        setFormComplete(true);
        return;
      case BANK_ACCOUNT_STEPS.ACCOUNT_CONFIRMATION:
        saveAccountByFormID();
        break;
      default:
        break;
    }
  };

  // TODO: Remove when ready to go live
  const isWhitelistEnabled = !!wiseEmailWhitelist?.length;
  const wiseEnabled =
    !isWhitelistEnabled || wiseEmailWhitelist?.includes(user.email);

  const originalTransferKind = getTransferKind(
    formIdentifier.countryCode,
    formIdentifier.bankCurrency,
  );
  const transferKind =
    originalTransferKind === CountryCode.US || wiseEnabled
      ? originalTransferKind
      : 'UNSUPPORTED';

  const renderForm = () => {
    if (!formIdentifier.countryCode && !formIdentifier.bankCurrency)
      return null;
    if (
      transferKind === CountryCode.US &&
      availableProviders.includes(PayoutProvider.DWOLLA)
    ) {
      return (
        <AddACHAccountContainer
          onBackClick={onBackClick}
          onAccountCreationSuccess={setPayoutAccount}
          handleFormComplete={setFormComplete}
          onSubmissionResult={setFormSubmissionResult}
          setDwollaSubmit={setSubmitDwolla}
          setErrorMsg={setError}
          setFormID={(id: FORM_ID) =>
            setSelectedForm((prev) => ({ ...prev, id }))
          }
        />
      );
    }

    if (transferKind === CountryCode.UK) {
      return (
        <AddPayoutAccountUKBankWrapper
          handleFormComplete={setFormComplete}
          handleFormChange={(payload: {
            id: FORM_ID;
            form: IBANForm | UKSortCodeBankForm;
          }) => {
            setSelectedForm(payload);
          }}
        />
      );
    }

    if (transferKind === CountryCode.EU && !!formIdentifier.bankCurrency) {
      return (
        <AddPayoutAccountIBANForm
          recipientCountry={formIdentifier.countryCode}
          currency={formIdentifier.bankCurrency}
          handleFormComplete={setFormComplete}
          handleFormChange={(payload: { id: FORM_ID; form: IBANForm }) => {
            setSelectedForm(payload);
          }}
        />
      );
    }

    return (
      <div>
        <Typography variant="h5">
          {t('sunshine.content.currency_transfer_not_supported')}
        </Typography>
      </div>
    );
  };

  const verifyPayee = () => {
    if (formSubmissionResult === 'confirmation_required') {
      return (
        <VerificationOfPayeeComponent
          verificationOutcomeReadOnly={recommendedUpdates}
        />
      );
    } else return null;
  };

  const renderAccountConfirmation = () => {
    return !error ? (
      <AddPayoutAccountConfirmation
        inputValues={[
          { label: t('label.account_type'), value: selectedForm.id },
          {
            label: t('sunshine.label.account_id'),
            value: payoutAccount?.providerCustomerID ?? '***',
          },
          ...(payoutAccount?.displayName
            ? [
                {
                  label: t('sunshine.label.account_details'),
                  value: payoutAccount.displayName,
                },
              ]
            : []),
        ]}
      />
    ) : (
      // TODO: some error container
      <>{error}</>
    );
  };

  return (
    <div className={styles.container}>
      <AddPayoutAccountBaseModule
        errorMsg={error}
        formID={selectedForm.id}
        formReady={formComplete}
        processingRequest={false}
        provider={provider}
        stepTitles={BANK_ACCOUNT_STEP_TITLES}
        resetErrorMsg={() => {}}
        exit={onBackClick}
        edit={(): void => setStep((prevState) => prevState - 1)}
        step={step}
        save={() => {
          void handleSave();
        }}
      >
        {step === BANK_ACCOUNT_STEPS.ACCOUNT_TYPE && (
          <>
            <AddPayoutAccountIdentifierForm
              handleFormChange={(payload) => setFormIdentifier(payload.form)}
              handleFormComplete={setFormComplete}
            />
          </>
        )}
        {step === BANK_ACCOUNT_STEPS.TRANSFER_DETAILS && renderForm()}
        {step === BANK_ACCOUNT_STEPS.VERIFY_PAYEE && verifyPayee()}
        {/*TODO: add what we actually want user to see/verify visually before saving*/}
        {step === BANK_ACCOUNT_STEPS.ACCOUNT_CONFIRMATION &&
          renderAccountConfirmation()}
      </AddPayoutAccountBaseModule>
    </div>
  );
};

export default AddBankAccountContainer;
